import { useSession } from '@/context/SessionProvider'
import { Box, Button, Grid, Skeleton, Tooltip } from '@chakra-ui/react'
import { useApolloClient } from '@apollo/client'
import { useEffect } from 'react'
import { GET_LAST_COINFLIP_GAME_BY_PLAYER } from '@/api/query'
import { delay } from '@/helpers/delay'
import { Image as ImageHOC } from '@/components/HOC/Image'
import GameBg from '@/public/assets/images/coinflip/coinflip-bg.jpg'
import { formatAmount } from '@/helpers/formatAmount'
import { CoinflipChoice, MiniGameStatus } from '@/__generated__/graphql'
import { useAppToast } from '@/hooks/useAppToast'
import { MultiplierCounter } from './MultiplierCounter'
import { Coin } from './Coin'
import { currenciesSettings, useCoinflipStore } from './CoinflipProvider'
import { GAME_MODE, GameStatus } from './types'
import { MultipliersTrack } from './MultipliersTrack'
import { HeadsOrTailsButtons } from './HeadsOrTailsButtons'
import { LastGames } from './LastGames'
import { RoundCounter } from './RoundCounter'
import { Form } from './Form'
import { BigNumberInBase } from '@injectivelabs/utils'
import { getTokenByDenom } from '@/services/token-service'
import { useBalanceStore } from '@/context/UserBalance'
import { useShallow } from 'zustand/react/shallow'
import { AnimationIcon } from '@/icons'
import { useTranslation } from 'next-i18next/pages'

const transformRounds = (rounds: Array<{ userChoice: CoinflipChoice; result: MiniGameStatus }>) => {
  return {
    currentStep: rounds.length,
    userChoices: rounds.map((round) => ({
      choice: round.userChoice,
      result: round.result,
    })),
  }
}

export const CoinflipTemplate = () => {
  const { t } = useTranslation()
  const { account, isInitialized: isAccountInitialized } = useSession()
  const { animationSpeed, initialize, resumeGame, isInitialized, boot, setValues, form } = useCoinflipStore()
  const appoloClient = useApolloClient()
  const { onError } = useAppToast()
  const isGameActive = useCoinflipStore(useShallow((state) => state.view.status === GameStatus.ACTIVE))

  useEffect(() => {
    const token = getTokenByDenom(form.fields.denom)
    useCoinflipStore.setState((state) => ({
      gameConfig: {
        ...state.gameConfig,
        maxWin: new BigNumberInBase(currenciesSettings.maxWin[GAME_MODE.MULTIPLY][token.currency]).toWei(
          token.decimals
        ),
      },
    }))
  }, [form.fields.mode, form.fields.denom, form.fields.amount])

  useEffect(() => {
    if (isGameActive) {
      useBalanceStore.getState().lockBalance()
    }

    return () => {
      useBalanceStore.getState().unLockBalance(true)
    }
  }, [isGameActive])

  const denom = useBalanceStore((state) => state.denom)
  useEffect(() => {
    useCoinflipStore.getState().setValue('denom', denom)
  }, [denom])

  useEffect(() => {
    if (!isInitialized) {
      const formSessionValue = sessionStorage.getItem('coinflip-form')
      if (formSessionValue) {
        const values = Object.fromEntries(
          Object.entries(JSON.parse(formSessionValue))
            .filter((x) => x[1])
            .map(([key, value]) => [key, { value, setDirty: false }])
        )
        useCoinflipStore.getState().setValues(values)
      }
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      boot()

      if (!account) {
        await delay(100)
        isAccountInitialized && initialize()
        return
      }

      try {
        const games = await appoloClient.query({
          query: GET_LAST_COINFLIP_GAME_BY_PLAYER,
          variables: {
            player: account.injectiveAddress,
          },
          fetchPolicy: 'no-cache',
        })

        const game = games.data.coinflipGames?.nodes[0]

        if (game) {
          setValues({
            amount: {
              value: Number(formatAmount(game.bet, { decimals: getTokenByDenom(game.denom!).decimals })).toString(),
              setDirty: false,
            },
            mode: {
              value: game.maxGameRound === 1 ? GAME_MODE.INSTANT : GAME_MODE.MULTIPLY,
              setDirty: false,
            },
            denom: {
              value: game.denom!,
              setDirty: false,
            },
          })

          useBalanceStore.setState({ denom: game.denom! })

          const rounds = transformRounds(game.coinflipGameRoundsByGameId.nodes as any)
          resumeGame({
            gameId: game.id,
            rounds: rounds as any,
            lastChoice: rounds.userChoices[0].choice,

            gameConfig: {
              maxGameRound: game.maxGameRound,
              rtp: game.rtp,
              maxWin: game.maxWin,
            },
          })
        } else {
          initialize()
        }
      } catch (e) {
        onError({ custom: { title: t('messages.unable-to-connect') } })
      }
    })()
  }, [account, isAccountInitialized])

  if (!isInitialized) {
    return (
      <Grid gridTemplateColumns={{ base: '1fr', xl: 'auto 320px' }} gap={5}>
        <Skeleton startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} height={{ base: '642px', md: '656px' }} />
        <Skeleton startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} height={{ base: '642px', md: '656px' }} />
      </Grid>
    )
  }

  return (
    <>
      {/* <ChallangeBanner /> */}
      <Grid gridTemplateColumns={{ base: '1fr', xl: 'auto 320px' }} gap={5}>
        <Box p={{ base: 0, md: 5 }} rounded={'2xl'} pos={'relative'} overflow={'hidden'}>
          <ImageHOC
            fill={'auto'}
            quality={90}
            objectFit="cover"
            priority={true}
            loading="eager"
            src={GameBg}
            alt={''}
          />
          <Box pos={'relative'} zIndex={10}>
            <Tooltip
              fontSize={'sm'}
              color={'ninja.100'}
              bg={'ninja.500'}
              rounded={'lg'}
              label={t('coinflip.animations', { state: animationSpeed == 1 ? t('common.on') : t('common.off') })}
              p={2}
            >
              <Box
                color={animationSpeed === 1 ? 'brand.white' : 'ninja.300'}
                gap={1}
                area-label="Animation"
                cursor={'pointer'}
                background="ninja.100/40"
                _hover={{ background: 'ninja.200' }}
                rounded={'xl'}
                onClick={() =>
                  useCoinflipStore.setState({
                    animationSpeed: animationSpeed === 0 ? 1 : 0,
                  })
                }
                as={AnimationIcon}
              ></Box>{' '}
            </Tooltip>

            <Grid
              mb={5}
              mt={14}
              gridTemplateColumns={{ base: '1fr 112px 1fr', md: '1fr 160px 1fr' }}
              gap={5}
              mx={'auto'}
            >
              <RoundCounter />
              <Coin />
              <MultiplierCounter />
            </Grid>
            <HeadsOrTailsButtons />
            <MultipliersTrack />
            <Form />
          </Box>
        </Box>
        <LastGames />
      </Grid>
    </>
  )
}

// import BannerBg from '@/public/assets/images/coinflip/banner-bg.png'
// import BannerGraphic from '@/public/assets/images/coinflip/banner-graphic.png'
// import { Image as ImageHoc } from '@/components/HOC/Image'

// const ChallangeBanner = () => {
//   const modal = useAppModal()
//   return (
//     <Flex
//       onClick={() => modal.openModalNew(AppModals.CoinflipContestModal)}
//       mb={6}
//       overflow={'hidden'}
//       pos={'relative'}
//       py={{ base: 2.5, md: 4 }}
//       alignItems={'center'}
//       justifyContent={'center'}
//       rounded={'xl'}
//       cursor={'pointer'}
//     >
//       <Box maxW={{ base: '200px', lg: '250px', xl: '460px' }} pos={'relative'} zIndex={10}>
//         <Text
//           lineHeight={{ base: 1.1, md: 'auto' }}
//           textAlign={'center'}
//           fontWeight={700}
//           fontSize={{ base: 'md', lg: 'lg' }}
//         >
//           Collect tickets for{' '}
//           <Text color={'#5D9FFF'} as={'span'}>
//             the lottery with a 25 $INJ pool!
//           </Text>
//         </Text>
//         <Text textAlign={'center'} color={'ninja.200'} fontSize={'xs'}>
//           Each $NBZ token you bet qualifies you to enter the lottery.
//         </Text>
//       </Box>
//       <ImageHoc pos={'absolute'} w={'full'} h={'full'} inset={0} objectFit={'cover'} src={BannerBg} alt={''} />
//       <ImageHoc
//         left={{ base: -150, sm: -20, md: -20, xl: 0 }}
//         w={{ base: '205px', md: '340px' }}
//         pos={'absolute'}
//         src={BannerGraphic}
//         alt={''}
//       />
//       <ImageHoc
//         right={{ base: -150, sm: -20, md: -20, xl: 0 }}
//         w={{ base: '205px', md: '340px' }}
//         transformOrigin={'center'}
//         pos={'absolute'}
//         src={BannerGraphic}
//         alt={''}
//       />
//     </Flex>
//   )
// }
